import { InterpolatePrismicDocumentError } from './InterpolatePrismicDocumentError'
import { InterpolatePrismicDocumentInvalidFields } from './InterpolatePrismicDocumentInvalidFields'
import { PrismicDocument } from '@prismicio/client'
import {
  RenderStringContext,
  RenderStringError,
  RenderStringOptions,
  renderString
} from '@dtx-company/true-common/src/utils/strings/renderString'
import isPlainObject from 'lodash/isPlainObject'

function interpolateStringValue(
  key: string,
  value: string,
  context: RenderStringContext,
  options: RenderStringOptions,
  invalidFields: InterpolatePrismicDocumentInvalidFields
): string {
  try {
    return renderString(value, context, options)
  } catch (error) {
    if (error instanceof RenderStringError) {
      // Record the error, return the original value without interpolation
      // so we can keep collecting more errors
      invalidFields[key] = {
        invalidPlaceholders: error.invalidPlaceholders,
        invalidFilters: error.invalidFilters
      }
    } else {
      // Unexpected errors do throw and abort things
      throw error
    }
  }
  return value
}

function interpolateKeys(
  currentObject: Record<string, unknown>,
  newObject: Record<string, unknown>,
  context: RenderStringContext,
  options: RenderStringOptions,
  invalidFields: InterpolatePrismicDocumentInvalidFields
): void {
  Object.keys(currentObject).forEach(key => {
    const value = currentObject[key]
    if (typeof value === 'string') {
      newObject[key] = interpolateStringValue(key, value, context, options, invalidFields)
    } else if (Array.isArray(value)) {
      newObject[key] = value.map(item => {
        if (typeof item === 'string') {
          return interpolateStringValue(key, item, context, options, invalidFields)
        } else if (isPlainObject(item)) {
          Object.keys(item).forEach(itemKey => {
            const itemValue = item[itemKey]
            if (typeof itemValue === 'string') {
              item[itemKey] = interpolateStringValue(
                key,
                itemValue,
                context,
                options,
                invalidFields
              )
            }
          })
        }
        return item
      })
    }
  })
}

/**
 * Interpolates template strings inside of a prismic document with the values in the given
 * context object and returns a new Prismic Document.
 *
 * Does not currently work with RichText fields.
 * Does not currently support slices.
 *
 * @see renderString
 */
export function interpolatePrismicDocument<DocType extends PrismicDocument = PrismicDocument>(
  prismicDocument: DocType,
  context: RenderStringContext,
  options: RenderStringOptions = {}
): DocType {
  const invalidFields: InterpolatePrismicDocumentInvalidFields = {}
  const newDocument = {
    ...prismicDocument,
    data: {
      ...prismicDocument.data
    }
  }
  interpolateKeys(prismicDocument.data, newDocument.data, context, options, invalidFields)
  if (Object.keys(invalidFields).length) {
    throw new InterpolatePrismicDocumentError({ invalidFields })
  }
  return newDocument
}
